// Notifications with the board closed (Features 1, 20 Oct): this browser signs up with its push service once the user
// allows notifications, and the board pushes each notice to it (worker/push.ts). The service worker (public/sw.js)
// shows it and opens what it is about. Signing out lets this browser's subscription go, so a shared computer stops
// getting the board's notices.

import type { ID } from "../../shared/types";
import { api } from "../lib/api";
import { openNotice } from "./notices";
import { openPlace } from "./place";
import { get } from "./store";

/** Whether this browser can take notifications with the board closed (an iPhone's Safari only from the Home Screen). */
export const pushSupported = () => typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && typeof Notification !== "undefined";

const keyBytes = (b64: string) => {
  const t = b64.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(t + "=".repeat((4 - (t.length % 4)) % 4));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
};

const same = (a: ArrayBuffer | null | undefined, b: Uint8Array) => !!a && a.byteLength === b.length && new Uint8Array(a).every((x, i) => x === b[i]);

let started: Promise<boolean> | null = null;

/**
 * Signs this browser up, when notifications are allowed here and on in Settings: its service worker, then its
 * subscription with the board's key (made again if the key changed), told to the board. Once a page load.
 */
export function ensurePush(): Promise<boolean> {
  if (!pushSupported() || Notification.permission !== "granted") return Promise.resolve(false);
  return (started ??= (async () => {
    try {
      const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      await navigator.serviceWorker.ready;
      const { key } = await api<{ key: string }>("/api/push/key");
      const want = keyBytes(key);
      let sub = await reg.pushManager.getSubscription();
      if (sub && !same(sub.options.applicationServerKey, want)) {
        await sub.unsubscribe().catch(() => false);
        sub = null;
      }
      sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: want });
      await api("/api/push/subscribe", { body: { sub: sub.toJSON() } });
      return true;
    } catch (e) {
      console.warn("push", (e as Error).message);
      started = null;
      return false;
    }
  })());
}

/** Lets this browser's subscription go: on signing out, and with the board told while the session still works. */
export async function stopPush(tellBoard: boolean) {
  started = null;
  if (!pushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration("/");
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return;
    if (tellBoard) await api("/api/push/unsubscribe", { body: { endpoint: sub.endpoint } }).catch(() => null);
    await sub.unsubscribe();
  } catch {
    /* nothing kept here to let go */
  }
}

/** Asks to allow notifications here, then signs up. */
export async function allowHere(): Promise<NotificationPermission> {
  const p = await Notification.requestPermission();
  if (p === "granted") await ensurePush();
  return p;
}

/** A notice to open, from a click on a push: by its id once the board has it, else where it points. */
function openFromPush(id: ID | null, to: unknown) {
  const n = id ? get().notices.find((x) => x.id === id) : null;
  if (n) {
    openNotice(n);
    return;
  }
  if (to && typeof to === "object" && typeof (to as { board?: unknown }).board === "string") openPlace(to as never);
}

/** The service worker's word that a notification was clicked, and a board opened from one with ?notice=. */
export function listenForPushClicks() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  navigator.serviceWorker.addEventListener("message", (e: MessageEvent) => {
    const m = e.data as { type?: string; id?: ID | null; to?: unknown } | null;
    if (m?.type === "cb:notice") openFromPush(m.id ?? null, m.to);
  });
  const id = new URLSearchParams(location.search).get("notice");
  if (id === null) return;
  const url = new URL(location.href);
  url.searchParams.delete("notice");
  history.replaceState(history.state, "", url.pathname + url.search + url.hash);
  if (id) openFromPush(id, null);
}
